// Anke, 2026-07-19 — opening a link from a textbox.
//
// Separated from the renderer so the scheme check is testable on its own and
// impossible to skip: every path that opens a textbox link goes through here.

import type { LinkTarget } from './textbox-svg-renderer.js';

/**
 * Schemes we are willing to hand to `window.open`.
 *
 * A SECOND line of defence, not the first: TipTap's autolinker only ever
 * produces http/https, and its `isAllowedUri` already refuses `javascript:`.
 * But a textbox document can also arrive by PASTE, and pasted HTML can carry an
 * `<a href>` that no autolinker ever inspected — so the value is re-checked at
 * the moment it would actually be executed.
 */
const ALLOWED_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:']);

/**
 * The URL if it is safe to open, otherwise `null`.
 *
 * Relative and malformed values return `null` rather than being resolved
 * against the host page: a textbox link that does not say where it goes should
 * go nowhere.
 */
export function safeLinkUrl(href: string): string | null {
  const trimmed = href.trim();
  if (trimmed === '') return null;
  try {
    const url = new URL(trimmed);
    return ALLOWED_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Open a textbox link in a new tab. Returns whether it actually opened, so a
 * caller can decide whether the click was consumed.
 *
 * `noopener,noreferrer` is not optional: without `noopener` the opened page can
 * reach back through `window.opener` and navigate the board away.
 */
export function openLinkUrl(href: string): boolean {
  const url = safeLinkUrl(href);
  if (url === null) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

/**
 * Whether a measured launcher is actually PAINTED, and therefore clickable.
 *
 * The measuring pass runs with the box height released, so it also reports
 * launchers that the rendered bitmap clips away. Those are not on screen and
 * must not be clickable — otherwise the empty space below a short box would
 * open links the user cannot see.
 */
export function isLauncherVisible(target: LinkTarget, boxHeight: number): boolean {
  return isInsideBoxHeight(target.y, target.height, boxHeight);
}

/**
 * Whether a measured rect (box-local `y`, `height`) lies fully inside the
 * painted box. Clipping happens at the bottom (overflowing content) AND, since
 * the `vAlign` offset of review 0034 (Anke, 2026-10-03), at the top: a middle-
 * or bottom-aligned box that overflows does so upwards, giving negative `y`.
 * Shared by the link and checklist hotspots.
 */
export function isInsideBoxHeight(y: number, height: number, boxHeight: number): boolean {
  return y >= 0 && y + height <= boxHeight;
}
